import { authModalsMarkup } from './markup/authModalsMarkup';
import { changePasswordModalMarkup } from './markup/profileMarkup';
import { resetPasswordMarkup } from './markup/resetPasswordMarkup';
import type { Root } from './prototypeTypes';

export const CREDENTIAL_MODAL_REGISTRY = [
	{ id: 'loginModal', source: authModalsMarkup },
	{ id: 'registerModal', source: authModalsMarkup },
	{ id: 'forgotModal', source: authModalsMarkup },
	{ id: 'oauthLinkModal', source: authModalsMarkup },
	{ id: 'resetPasswordOverlay', source: resetPasswordMarkup },
	{ id: 'changePasswordModal', source: changePasswordModalMarkup },
] as const;

export type CredentialModalId = (typeof CREDENTIAL_MODAL_REGISTRY)[number]['id'];

const credentialModalById = new Map<string, (typeof CREDENTIAL_MODAL_REGISTRY)[number]>(
	CREDENTIAL_MODAL_REGISTRY.map((entry) => [entry.id, entry]),
);

export function isCredentialModalId(id: string): id is CredentialModalId {
	return credentialModalById.has(id);
}

/**
 * Mounts exactly one credential dialog for the duration of its interaction.
 * Keeping password fields out of the resting business document prevents
 * browser password-manager heuristics from associating unrelated forms with
 * authentication while preserving the existing in-page Dialog experience.
 */
export function ensureCredentialModalMounted(root: Root, id: string): HTMLElement | null {
	const existing = root.querySelector<HTMLElement>(`#${id}`);
	if (existing) return existing;
	const entry = credentialModalById.get(id);
	if (!entry) return null;

	const host = root instanceof Document ? root.body : root;
	const template = host.ownerDocument.createElement('template');
	template.innerHTML = entry.source;
	const modal = template.content.querySelector<HTMLElement>(`#${id}`);
	if (!modal) return null;

	modal.hidden = true;
	modal.setAttribute('aria-hidden', 'true');
	modal.setAttribute('inert', '');
	modal.dataset.credentialModalMount = 'lazy';
	host.appendChild(modal);
	return modal;
}

/** Removes only lifecycle-owned dialogs; static test fixtures remain intact. */
export function unmountCredentialModal(root: Root, id: string): void {
	const modal = root.querySelector<HTMLElement>(`#${id}`);
	if (modal?.dataset.credentialModalMount === 'lazy') modal.remove();
}
